import React, { useState } from 'react';
import { ChevronDown, ChevronUp, FileText } from 'lucide-react';

const TermsAndConditions = ({ collapsed = true, className = '' }) => {
  const [isExpanded, setIsExpanded] = useState(!collapsed);

  const termsContent = (
    <div className="prose prose-sm max-w-none text-stone-600">
      <h3 className="text-lg font-semibold text-stone-800 mt-0">Terms & Conditions of Sale</h3>
      <p className="text-xs text-stone-500 italic">(Made-to-Measure Shutters – In-Home Consultation Sales)</p>

      <h4 className="text-sm font-semibold text-stone-700 mt-4">1. About Us</h4>
      <p className="text-sm">
        These Terms & Conditions govern all contracts for the supply and installation of made-to-measure shutters by Aston Lily Shutters ("we", "us", "our").
        All sales are conducted following a consultation in your home. By proceeding with an order, you agree to be bound by these Terms.
      </p>

      <h4 className="text-sm font-semibold text-stone-700 mt-4">2. Consultation, Quotation & Order Confirmation</h4>
      <ul className="text-sm list-disc pl-5 space-y-1">
        <li>Following your in-home consultation, we will provide a written quotation outlining your chosen products and specifications.</li>
        <li>Quotations remain valid for 30 days, unless otherwise stated.</li>
        <li>An order is confirmed once we have received your deposit and issued written confirmation.</li>
        <li>By confirming your order, you accept responsibility for the agreed design, finish, and specification.</li>
      </ul>

      <h4 className="text-sm font-semibold text-stone-700 mt-4">3. Bespoke Nature of Our Products</h4>
      <p className="text-sm">
        Each shutter we supply is individually crafted to order, tailored precisely to your property and design preferences.
        As a result, once your order has entered production, it cannot be amended, cancelled, or refunded.
      </p>

      <h4 className="text-sm font-semibold text-stone-700 mt-4">4. Cooling-Off Period</h4>
      <p className="text-sm">
        As your order is placed following an in-home consultation, it falls under the Consumer Contracts (Information, Cancellation and Additional Charges) Regulations 2013.
      </p>
      <p className="text-sm">
        While these regulations typically provide a 14-day cancellation period, this does not apply to our products, as they are bespoke and made to your specific measurements and requirements.
      </p>
      <p className="text-sm font-medium text-stone-700">
        By confirming your order, you expressly acknowledge and agree that your right to cancel within the 14-day cooling-off period is waived once production commences.
      </p>

      <h4 className="text-sm font-semibold text-stone-700 mt-4">5. Lead Times</h4>
      <ul className="text-sm list-disc pl-5 space-y-1">
        <li>Our typical lead time is 10–12 weeks from order confirmation.</li>
        <li>However, as each shutter is made to order, all lead times are indicative estimates only. Timings may vary due to factors beyond our control, including manufacturing schedules, international shipping, and supply chain conditions.</li>
        <li>We will always endeavour to keep you informed, but we cannot guarantee completion within a fixed timeframe and shall not be liable for delays outside our reasonable control.</li>
      </ul>

      <h4 className="text-sm font-semibold text-stone-700 mt-4">6. Payment Terms</h4>
      <ul className="text-sm list-disc pl-5 space-y-1">
        <li>A deposit is required to secure your order.</li>
        <li>The remaining balance is payable prior to installation, unless otherwise agreed in writing.</li>
        <li>We reserve the right to delay delivery or installation until full payment has been received.</li>
      </ul>

      <h4 className="text-sm font-semibold text-stone-700 mt-4">7. Survey & Measurements</h4>
      <ul className="text-sm list-disc pl-5 space-y-1">
        <li>Following your order, a detailed survey will be carried out where required to ensure precise measurements.</li>
        <li>Where measurements are taken by us, we accept responsibility for their accuracy.</li>
        <li>Any subsequent changes requested after survey may incur additional costs and affect lead times.</li>
      </ul>

      <h4 className="text-sm font-semibold text-stone-700 mt-4">8. Installation</h4>
      <ul className="text-sm list-disc pl-5 space-y-1">
        <li>Installation will be arranged once your shutters have arrived and passed quality checks.</li>
        <li>You agree to provide safe, clear, and reasonable access to the installation areas.</li>
        <li>Any unforeseen site conditions or preparatory works required may result in additional charges, which will be discussed with you in advance wherever possible.</li>
      </ul>

      <h4 className="text-sm font-semibold text-stone-700 mt-4">9. Natural Materials & Finishes</h4>
      <p className="text-sm">
        Many of our shutters are crafted from natural materials. Subtle variations in colour, grain, and texture are part of their inherent character and should be regarded as a feature of a premium, handcrafted product rather than a defect.
      </p>
      <p className="text-sm">Samples and showroom displays are indicative and may not represent an exact match.</p>

      <h4 className="text-sm font-semibold text-stone-700 mt-4">10. Warranty</h4>
      <p className="text-sm">We are proud to offer extended warranties that reflect the quality of our products:</p>
      <ul className="text-sm list-disc pl-5 space-y-1">
        <li><strong>10-Year Warranty</strong> on all our shutter ranges</li>
        <li><strong>10-Year Warranty</strong> on our budget shutter range</li>
      </ul>
      <p className="text-sm mt-2">
        These warranties cover manufacturing defects under normal residential use.
        They do not cover damage resulting from misuse, improper maintenance, environmental conditions, or unauthorised alterations.
      </p>

      <h4 className="text-sm font-semibold text-stone-700 mt-4">11. Defects & Aftercare</h4>
      <ul className="text-sm list-disc pl-5 space-y-1">
        <li>Any concerns must be reported within 7 days of installation.</li>
        <li>We will assess and, where appropriate, repair or replace any defective components in line with your statutory rights under the Consumer Rights Act 2015.</li>
      </ul>

      <h4 className="text-sm font-semibold text-stone-700 mt-4">12. Limitation of Liability</h4>
      <p className="text-sm">
        To the fullest extent permitted by law, our liability is limited to the value of the goods supplied.
        We shall not be liable for any indirect or consequential losses, including loss of use or inconvenience arising from delays.
      </p>

      <h4 className="text-sm font-semibold text-stone-700 mt-4">13. Force Majeure</h4>
      <p className="text-sm">
        We shall not be held responsible for any delay or failure to perform our obligations where such delay arises from events beyond our reasonable control, including but not limited to manufacturing disruption, shipping delays, or global supply issues.
      </p>

      <h4 className="text-sm font-semibold text-stone-700 mt-4">14. Governing Law</h4>
      <p className="text-sm">
        These Terms & Conditions are governed by the laws of England and Wales, and any disputes shall be subject to the exclusive jurisdiction of its courts.
      </p>
    </div>
  );

  if (collapsed) {
    return (
      <div className={`border border-stone-200 rounded-lg bg-stone-50 ${className}`}>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-stone-100 transition-colors rounded-lg"
        >
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-stone-500" />
            <span className="font-medium text-stone-700">Terms & Conditions of Sale</span>
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-stone-500" />
          ) : (
            <ChevronDown className="w-5 h-5 text-stone-500" />
          )}
        </button>
        {isExpanded && (
          <div className="px-4 pb-4 border-t border-stone-200 pt-4 max-h-96 overflow-y-auto">
            {termsContent}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      {termsContent}
    </div>
  );
};

export default TermsAndConditions;
